/**
 * Read/Write Patterns (doc 147 §6.6 E2) — per-entity Cloud Function touchpoint
 * counts. Read-heavy entities are caching candidates; write-heavy are
 * sharding candidates.
 */
import type { AtlasManifest } from '@atlas-manifest/types';

export type RWClassification =
  | 'balanced'
  | 'caching-candidate'
  | 'sharding-candidate'
  | 'untouched';

export interface EntityRwPattern {
  entityId: string;
  reads: number;
  writes: number;
  total: number;
  readWriteRatio: number;
  classification: RWClassification;
}

export const READ_HEAVY_THRESHOLD = 10;
export const WRITE_HEAVY_THRESHOLD = 10;

export function readWritePatterns(manifest: AtlasManifest): EntityRwPattern[] {
  const reads = new Map<string, number>();
  const writes = new Map<string, number>();
  for (const cf of manifest.cloudFunctions) {
    for (const e of cf.reads) reads.set(e, (reads.get(e) ?? 0) + 1);
    for (const e of cf.writes) writes.set(e, (writes.get(e) ?? 0) + 1);
  }
  const result = manifest.entities.map((entity) => {
    const r = reads.get(entity.id) ?? 0;
    const w = writes.get(entity.id) ?? 0;
    const total = r + w;
    const ratio = w > 0 ? r / w : r;
    let classification: RWClassification = 'balanced';
    if (total === 0) classification = 'untouched';
    else if (r >= READ_HEAVY_THRESHOLD && r > w * 4) classification = 'caching-candidate';
    else if (w >= WRITE_HEAVY_THRESHOLD && w > r * 4) classification = 'sharding-candidate';
    return {
      entityId: entity.id,
      reads: r,
      writes: w,
      total,
      readWriteRatio: ratio,
      classification,
    };
  });
  return result.sort((a, b) => b.total - a.total);
}
